import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

/**
 * RING-TIER-03 · Feature 6 + Sprint A close-out — Admin dashboard.
 *
 * Read-only listings + two mutations that never touch founder data:
 *   1. Regenerate — renders the four Tier 1 documents in memory,
 *      validates each artifact, appends a version stamp, and streams the
 *      zip back to the browser as a download. Admin bypass — no
 *      tier1_complete gate, any ring state accepted.
 *   2. Backfill/regen — endpoint mirror of scripts/tier1_backfill_and_regen.py.
 *      Supports a dry_run switch so an operator can preview counts before
 *      committing writes.
 */
export default function Admin() {
  const [users, setUsers] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busyIdea, setBusyIdea] = useState(null);
  const [regenError, setRegenError] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupError, setBackupError] = useState(null);
  const [dbInfo, setDbInfo] = useState(null);
  const [dbInfoBusy, setDbInfoBusy] = useState(false);
  const [dbInfoError, setDbInfoError] = useState(null);
  const [diagIdea, setDiagIdea] = useState(null);
  const [diagJson, setDiagJson] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagError, setDiagError] = useState(null);
  const diagRef = useRef(null);
  const [auditJson, setAuditJson] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  // Reconcile Uploads + Panel Re-run — new admin actions added Feb 2026.
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileError, setReconcileError] = useState(null);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [panelRerunId, setPanelRerunId] = useState("");
  const [panelRerunBusy, setPanelRerunBusy] = useState(false);
  const [panelRerunError, setPanelRerunError] = useState(null);
  const [panelRerunResult, setPanelRerunResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [u, i] = await Promise.all([
          api.get("/admin/users"),
          api.get("/admin/ideas"),
        ]);
        if (!alive) return;
        setUsers(u.data.users || []);
        setIdeas(i.data.ideas || []);
      } catch (e) {
        if (e?.response?.status === 403) setForbidden(true);
        else toast.error(e?.response?.data?.detail || "Admin load failed.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const regen = async (ideaId, title) => {
    setBusyIdea(ideaId);
    setRegenError(null);
    try {
      const r = await api.post(`/admin/ideas/${ideaId}/regenerate-package`, null, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      a.download = m ? m[1] : `${(title || "idea").replace(/[^\w-]+/g, "_")}-tier1-admin.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Regenerated · ${a.download}`);
    } catch (e) {
      // The endpoint is declared blob-responseType, so a JSON/HTML error body
      // arrives as a Blob and toast.error can't read .detail. Decode the
      // blob, try JSON, fall back to raw text. Persist the error in state so
      // it renders inline — a toast alone can be missed on a long admin page.
      const status = e?.response?.status || "network";
      let detail = e?.message || "Regenerate failed.";
      try {
        const body = e?.response?.data;
        const text = typeof body?.text === "function" ? await body.text() : null;
        if (text) {
          try { detail = JSON.parse(text).detail || text; }
          catch { detail = text; }
        } else if (typeof body === "string") {
          detail = body;
        }
      } catch { /* keep default */ }
      const errObj = {
        idea_id: ideaId,
        title,
        status,
        detail: (typeof detail === "string" ? detail : JSON.stringify(detail)).slice(0, 2000),
      };
      setRegenError(errObj);
      toast.error(`Regenerate ${errObj.status}: ${errObj.detail.slice(0, 140)}`);
    } finally {
      setBusyIdea(null);
    }
  };

  const runBackfill = async (dry_run) => {
    setBackfillBusy(true);
    setBackfillResult(null);
    try {
      const r = await api.post(`/admin/backfill-regen?dry_run=${dry_run ? "true" : "false"}`);
      setBackfillResult(r.data);
      toast.success(`Backfill ${dry_run ? "dry-run" : "run"} complete.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed.");
    } finally {
      setBackfillBusy(false);
    }
  };

  const runDiagnostic = async (ideaId, title) => {
    setDiagBusy(true);
    setDiagIdea({ id: ideaId, title });
    setDiagJson(null);
    setDiagError(null);
    // Panel renders below the Ideas table — scroll it into view so it
    // never looks like the click did nothing.
    requestAnimationFrame(() => {
      diagRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    try {
      const r = await api.get(`/admin/ideas/${ideaId}/diagnostic`);
      setDiagJson(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail
        || e?.response?.data
        || e?.message
        || "Diagnostic failed.";
      const errObj = {
        status: status || "network",
        detail: typeof detail === "string" ? detail : JSON.stringify(detail),
        url: e?.config?.url || `/admin/ideas/${ideaId}/diagnostic`,
      };
      setDiagError(errObj);
      toast.error(`Diagnostic ${errObj.status}: ${errObj.detail}`);
    } finally {
      setDiagBusy(false);
    }
  };

  const copyDiagnostic = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagJson, null, 2));
      toast.success("Copied to clipboard.");
    } catch { /* ignore */ }
  };

  const runThresholdAudit = async () => {
    setAuditBusy(true);
    try {
      const r = await api.get("/admin/threshold-audit");
      setAuditJson(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed.");
    } finally {
      setAuditBusy(false);
    }
  };

  if (loading) return <div className="p-12 font-mono text-slate-500">Loading…</div>;
  if (forbidden) {
    return (
      <div className="p-12 max-w-xl mx-auto" data-testid="admin-forbidden">
        <h1 className="text-2xl font-semibold">Admin access required</h1>
        <p className="mt-2 text-slate-600">Your account does not have the is_admin flag.</p>
      </div>
    );
  }

  const runDbInfo = async () => {
    setDbInfoBusy(true);
    setDbInfoError(null);
    setDbInfo(null);
    try {
      const r = await api.get("/admin/db-info");
      setDbInfo(r.data);
    } catch (e) {
      const status = e?.response?.status || "network";
      const detail = e?.response?.data?.detail || e?.message || "db-info failed";
      setDbInfoError({ status, detail: String(detail).slice(0, 400) });
    } finally {
      setDbInfoBusy(false);
    }
  };

  const runMongoBackup = async () => {
    setBackupBusy(true);
    setBackupError(null);
    try {
      const url = `${API}/admin/full-backup`;
      const res = await fetch(url, { method: "POST", credentials: "include" });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let detail = text;
        try { detail = JSON.parse(text).detail || text; } catch { /* keep raw */ }
        setBackupError({ status: res.status, detail: (detail || "backup failed").slice(0, 800) });
        toast.error(`Backup ${res.status}: ${(detail || "backup failed").slice(0, 140)}`);
        return;
      }
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `gauntlet-full-backup-${Date.now()}.tar.gz`;
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
      toast.success(`Backup downloaded · ${filename} · ${(blob.size / 1024).toFixed(1)} KB`);
    } catch (e) {
      const errObj = { status: "network", detail: e?.message || String(e) };
      setBackupError(errObj);
      toast.error(`Backup network error: ${errObj.detail}`);
    } finally {
      setBackupBusy(false);
    }
  };

  const runReconcileUploads = async () => {
    setReconcileBusy(true);
    setReconcileError(null);
    setReconcileResult(null);
    try {
      const r = await api.get("/admin/plan-uploads-reconcile");
      setReconcileResult(r.data);
      toast.success(
        r.data?.all_resolved
          ? "Reconcile: all plan_document rows resolve"
          : `Reconcile: ${r.data?.missing_count || 0} missing · ${r.data?.record_count || 0} rows`
      );
    } catch (e) {
      const status = e?.response?.status || "network";
      const detail = e?.response?.data?.detail || e?.message || "reconcile failed";
      setReconcileError({ status, detail: String(detail).slice(0, 800) });
    } finally {
      setReconcileBusy(false);
    }
  };

  const runPanelRerun = async () => {
    const ideaId = (panelRerunId || "").trim();
    if (!ideaId) {
      setPanelRerunError({ status: "input", detail: "Enter an idea id first." });
      return;
    }
    setPanelRerunBusy(true);
    setPanelRerunError(null);
    setPanelRerunResult(null);
    try {
      const r = await api.post(`/admin/ideas/${ideaId}/panel-rerun`);
      setPanelRerunResult(r.data);
      toast.success(`Panel re-run · history len=${r.data?.panel_history_length}`);
    } catch (e) {
      const status = e?.response?.status || "network";
      const detail = e?.response?.data?.detail || e?.message || "panel re-run failed";
      setPanelRerunError({ status, detail: String(detail).slice(0, 800) });
    } finally {
      setPanelRerunBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-8 space-y-8" data-testid="admin-page">
      <div>
        <div className="mono-caps text-xs text-slate-500">Read-only + tier-1 admin regen</div>
        <h1 className="text-3xl font-semibold">Admin dashboard</h1>
      </div>

      <section className="rounded-xl border-2 border-red-600 bg-red-50 p-5" data-testid="admin-mongo-backup-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="mono-caps text-[0.65rem] text-red-700">Break-glass · data safety</div>
            <h2 className="text-lg font-semibold text-red-900">Full backup — Mongo + plan_storage</h2>
            <p className="text-sm text-slate-700 mt-1 max-w-2xl">
              Runs <code>mongodump --gzip</code> and bundles it with the entire
              <code> plan_storage/</code> tree of founder-uploaded plan PDFs
              (inputs we cannot regenerate). Streams a single <code>.tar.gz</code>
              containing <code>mongo/*.archive.gz</code>, <code>plan_storage/**</code>,
              and a <code>manifest.json</code> with SHA-256s for each member.
              Never persists to the container filesystem.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={dbInfoBusy} onClick={runDbInfo}
              data-testid="admin-db-info-btn"
              className="px-4 py-2 rounded border-2 border-red-600 bg-white hover:bg-red-100 text-red-800 disabled:opacity-40">
              {dbInfoBusy ? "Checking…" : "Check DB"}
            </button>
            <button type="button" disabled={reconcileBusy} onClick={runReconcileUploads}
              data-testid="admin-reconcile-uploads-btn"
              className="px-4 py-2 rounded border-2 border-red-600 bg-white hover:bg-red-100 text-red-800 disabled:opacity-40">
              {reconcileBusy ? "Reconciling…" : "Reconcile Uploads"}
            </button>
            <a href="/admin/dossiers"
              data-testid="admin-dossiers-link"
              className="px-4 py-2 rounded border-2 border-slate-600 bg-white hover:bg-slate-100 text-slate-800 no-underline">
              Ring Dossiers →
            </a>
            <button type="button" disabled={backupBusy} onClick={runMongoBackup}
              data-testid="admin-mongo-backup-btn"
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
              {backupBusy ? "Dumping…" : "Take backup"}
            </button>
          </div>
        </div>
        {dbInfoError && (
          <div className="mt-3 bg-white border-2 border-red-500 rounded p-3 text-xs font-mono text-red-900 whitespace-pre-wrap break-words"
               data-testid="admin-db-info-error">
            <div className="mono-caps text-[0.65rem] text-red-700 mb-1">DB check failed · {dbInfoError.status}</div>
            {dbInfoError.detail}
          </div>
        )}
        {dbInfo && (
          <div className="mt-3 bg-white border-2 border-red-300 rounded p-3 text-xs font-mono text-slate-900 whitespace-pre-wrap break-words"
               data-testid="admin-db-info-result">
            <div className="mono-caps text-[0.65rem] text-red-700 mb-2">
              DB check · env={String(dbInfo.env_db_name)} · bound={String(dbInfo.motor_bound_db)}
              {" · "}match={dbInfo.env_matches_bound ? "yes" : "NO"}
            </div>
            <div className="mb-1"><b>Total documents:</b> {dbInfo.total_documents}</div>
            <div className="mb-2">
              {dbInfo.collections.map((c) => (
                <div key={c.name}>{c.name}: {c.doc_count}</div>
              ))}
            </div>
            <div>
              <b>plan_storage:</b> {dbInfo.plan_storage.exists_on_disk ? "on disk" : "MISSING FROM DISK"}
              {" · "}
              files={dbInfo.plan_storage.file_count} · bytes={dbInfo.plan_storage.total_bytes}
            </div>
            {dbInfo.process_started_at_utc && (
              <div className="mt-2 text-slate-500">process_started_at_utc: {dbInfo.process_started_at_utc}</div>
            )}
          </div>
        )}
        {backupError && (
          <div className="mt-3 bg-white border-2 border-red-500 rounded p-3 text-xs font-mono text-red-900 whitespace-pre-wrap break-words"
               data-testid="admin-mongo-backup-error">
            <div className="mono-caps text-[0.65rem] text-red-700 mb-1">Backup failed · {backupError.status}</div>
            {backupError.detail}
          </div>
        )}
        {reconcileError && (
          <div className="mt-3 bg-white border-2 border-red-500 rounded p-3 text-xs font-mono text-red-900 whitespace-pre-wrap break-words"
               data-testid="admin-reconcile-uploads-error">
            <div className="mono-caps text-[0.65rem] text-red-700 mb-1">Reconcile failed · {reconcileError.status}</div>
            {reconcileError.detail}
          </div>
        )}
        {reconcileResult && (
          <div className="mt-3 bg-white border-2 border-red-300 rounded p-3 text-xs font-mono text-slate-900 whitespace-pre-wrap break-words"
               data-testid="admin-reconcile-uploads-result">
            <div className="mono-caps text-[0.65rem] text-red-700 mb-2">
              Reconcile · records={reconcileResult.record_count} · r2={reconcileResult.resolves_r2} · disk={reconcileResult.resolves_disk} · missing={reconcileResult.missing_count}
            </div>
            <div className="mb-1">
              <b>Bucket:</b> {reconcileResult.bucket?.name || "(unset)"} · objects={reconcileResult.bucket?.count ?? "?"}
              {reconcileResult.bucket?.error ? ` · error=${reconcileResult.bucket.error}` : ""}
              {" · "}orphans_no_record={reconcileResult.bucket?.orphans_total ?? 0}
            </div>
            <div className="mb-1">
              <b>Status:</b> {reconcileResult.all_resolved ? "all_resolved" : (reconcileResult.mismatch ? "MISMATCH" : "ok")}
            </div>
            {reconcileResult.missing_records?.length > 0 && (
              <div className="mt-2">
                <div className="mono-caps text-[0.65rem] text-red-700 mb-1">Missing records</div>
                {reconcileResult.missing_records.slice(0, 25).map((r) => (
                  <div key={r.id}>{r.id} · {r.title} · key={r.r2_key || r.storage_ref}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="rounded-xl border-2 border-amber-600 bg-amber-50 p-5" data-testid="admin-panel-rerun-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="mono-caps text-[0.65rem] text-amber-800">Break-glass · investor readiness</div>
            <h2 className="text-lg font-semibold text-amber-900">Re-run Shark Panel</h2>
            <p className="text-sm text-slate-700 mt-1 max-w-2xl">
              Runs the 10-persona Shark Panel against any idea id that has reached Ring 4.
              The previous <code>panel_results</code> is <code>$push</code>-ed onto{" "}
              <code>panel_history</code> before this run's result is written, so any earlier
              baseline stays intact by construction. No operator should have to remember{" "}
              <code>POST /api/ideas/&#123;id&#125;/panel</code>.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <input
              type="text"
              placeholder="idea id (uuid)"
              value={panelRerunId}
              onChange={(e) => setPanelRerunId(e.target.value)}
              data-testid="admin-panel-rerun-idea-input"
              className="px-3 py-2 rounded border-2 border-amber-600 bg-white text-slate-900 text-sm font-mono min-w-[280px]"
            />
            <button
              type="button"
              disabled={panelRerunBusy || !panelRerunId.trim()}
              onClick={runPanelRerun}
              data-testid="admin-panel-rerun-btn"
              className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40"
            >
              {panelRerunBusy ? "Running…" : "Re-run Panel"}
            </button>
          </div>
        </div>
        {panelRerunError && (
          <div className="mt-3 bg-white border-2 border-amber-500 rounded p-3 text-xs font-mono text-amber-900 whitespace-pre-wrap break-words"
               data-testid="admin-panel-rerun-error">
            <div className="mono-caps text-[0.65rem] text-amber-800 mb-1">Panel re-run failed · {panelRerunError.status}</div>
            {panelRerunError.detail}
          </div>
        )}
        {panelRerunResult && (
          <div className="mt-3 bg-white border-2 border-amber-300 rounded p-3 text-xs font-mono text-slate-900 whitespace-pre-wrap break-words"
               data-testid="admin-panel-rerun-result">
            <div className="mono-caps text-[0.65rem] text-amber-800 mb-2">
              Panel re-run · idea={panelRerunResult.idea_id} · history_len={panelRerunResult.panel_history_length}
            </div>
            <div className="mb-1">
              <b>readiness:</b> {panelRerunResult.panel?.readiness_label} · band={panelRerunResult.panel?.readiness_band}
              {" · "}total_passed={panelRerunResult.panel?.total_passed}/{panelRerunResult.panel?.total_applicable}
            </div>
            <div className="mb-1">
              <b>diff:</b> new_pass={panelRerunResult.panel_diff?.totals?.new_pass ?? 0} · new_open={panelRerunResult.panel_diff?.totals?.new_open ?? 0}
              {" · "}prev={panelRerunResult.panel_diff?.totals?.prev_total ?? 0} → curr={panelRerunResult.panel_diff?.totals?.curr_total ?? 0}
            </div>
            <div className="mt-2 text-slate-500 text-[0.7rem]">{panelRerunResult.note}</div>
          </div>
        )}
      </section>

      <section className="rounded-xl border-2 border-violet-500 bg-violet-50 p-5" data-testid="admin-backfill-panel">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="mono-caps text-[0.65rem] text-violet-700">Production tool</div>
            <h2 className="text-lg font-semibold text-violet-900">Backfill + regenerate every Tier-1 idea</h2>
            <p className="text-sm text-slate-700 mt-1 max-w-2xl">
              Flips <code>tier_status.tier1_complete</code> on every idea whose Ring 3 score is ≥ 80,
              then renders the current four-document Tier 1 package for each — verifying every artifact.
              Failures name the specific document and exception. Dry-run reports counts without writing.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={auditBusy} onClick={runThresholdAudit}
              data-testid="admin-threshold-audit-btn"
              className="px-4 py-2 rounded border-2 border-amber-500 bg-white hover:bg-amber-100 text-amber-900 disabled:opacity-40">
              {auditBusy ? "…" : "Threshold audit"}
            </button>
            <button type="button" disabled={backfillBusy} onClick={() => runBackfill(true)}
              data-testid="admin-backfill-dryrun-btn"
              className="px-4 py-2 rounded border-2 border-violet-500 bg-white hover:bg-violet-100 text-violet-900 disabled:opacity-40">
              {backfillBusy ? "…" : "Dry-run"}
            </button>
            <button type="button" disabled={backfillBusy} onClick={() => runBackfill(false)}
              data-testid="admin-backfill-run-btn"
              className="px-4 py-2 rounded bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-40">
              {backfillBusy ? "…" : "Run"}
            </button>
          </div>
        </div>

        {auditJson && (
          <div className="mt-4 bg-white rounded-lg p-4 text-sm" data-testid="admin-threshold-audit-result">
            <div className="mono-caps text-[0.65rem] text-amber-700 mb-2">
              Threshold audit · current threshold = {auditJson.current_threshold} · total ideas = {auditJson.total_ideas}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <ul className="space-y-0.5">
                <li>Ring 3 ≥ {auditJson.current_threshold}: <b>{auditJson.counts.ring3_at_or_above_current_threshold}</b></li>
                <li>Ring 3 · 65 – 79 (window): <b className="text-amber-800">{auditJson.counts.ring3_between_65_and_79_inclusive}</b></li>
                <li>Ring 3 · 1 – 64: <b>{auditJson.counts.ring3_below_65}</b></li>
                <li>Advanced past R3 with Ring 3 &lt; {auditJson.current_threshold}: <b className="text-red-700">{auditJson.counts.advanced_past_ring3_below_current_threshold}</b></li>
              </ul>
              <ul className="space-y-0.5">
                <li>Legacy <code>idea.verdict</code> populated: <b>{auditJson.deprecated_verdict_field.any_value_populated}</b></li>
                <li>&nbsp;&nbsp;GO: <b>{auditJson.deprecated_verdict_field.GO}</b> · CONDITIONAL: <b>{auditJson.deprecated_verdict_field.CONDITIONAL}</b> · NO-GO: <b>{auditJson.deprecated_verdict_field.NO_GO}</b></li>
              </ul>
            </div>
            {auditJson.window_ideas_65_to_79.length > 0 && (
              <div className="border-t border-slate-200 pt-3">
                <div className="mono-caps text-[0.65rem] text-amber-700 mb-2">
                  Ideas in the 65–79 window ({auditJson.window_ideas_65_to_79.length}):
                </div>
                <ul className="space-y-1">
                  {auditJson.window_ideas_65_to_79.map((it) => (
                    <li key={it.id} className="font-mono text-xs">
                      R3={it.ring3_score?.score} · at R{it.current_ring} · {it.title} · verdict={String(it.verdict)} · tier1_complete={String((it.tier_status||{}).tier1_complete)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {auditJson.advanced_past_ring3_ideas && auditJson.advanced_past_ring3_ideas.length > 0 && (
              <div className="border-t border-slate-200 pt-3 mt-3" data-testid="admin-threshold-audit-advanced-leak">
                <div className="mono-caps text-[0.65rem] text-red-700 mb-2">
                  Gate-enforcement leak — advanced past Ring 3 with score &lt; {auditJson.current_threshold} ({auditJson.advanced_past_ring3_ideas.length}):
                </div>
                <ul className="space-y-1">
                  {auditJson.advanced_past_ring3_ideas.map((it) => (
                    <li key={it.id} className="font-mono text-xs text-red-800">
                      R3={it.ring3_score?.score} · at R{it.current_ring} · {it.title} · status={it.status} · tier1_complete={String((it.tier_status||{}).tier1_complete)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {backfillResult && (
          <div className="mt-4 space-y-3" data-testid="admin-backfill-result">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white rounded-lg p-3">
                <div className="mono-caps text-[0.65rem] text-slate-500">Step 1 · flag backfill</div>
                <ul className="mt-1 space-y-0.5">
                  <li>found: <b>{backfillResult.step1.found}</b></li>
                  <li>already set: <b>{backfillResult.step1.already_set}</b></li>
                  <li>set by this run: <b>{backfillResult.step1.set_now}</b></li>
                </ul>
              </div>
              <div className="bg-white rounded-lg p-3">
                <div className="mono-caps text-[0.65rem] text-slate-500">Step 2 · regeneration</div>
                <ul className="mt-1 space-y-0.5">
                  <li>attempted: <b>{backfillResult.step2.attempted}</b></li>
                  <li>succeeded: <b className="text-emerald-700">{backfillResult.step2.succeeded}</b></li>
                  <li>failed: <b className={backfillResult.step2.failed ? "text-red-700" : ""}>{backfillResult.step2.failed}</b></li>
                </ul>
              </div>
            </div>
            {backfillResult.step2.failures?.length > 0 && (
              <div className="bg-white rounded-lg p-3 text-sm" data-testid="admin-backfill-failures">
                <div className="mono-caps text-[0.65rem] text-red-700">Failures</div>
                <ul className="mt-2 space-y-2">
                  {backfillResult.step2.failures.map((f) => (
                    <li key={f.id} className="border-l-4 border-red-500 pl-3">
                      <div className="font-semibold">{f.title || "(untitled)"} <span className="mono-caps text-[0.6rem] text-slate-500">· {f.id}</span></div>
                      <div className="text-xs text-slate-600">doc: <code>{f.doc}</code></div>
                      <div className="text-xs text-slate-600 mt-0.5 font-mono">{f.reason}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {backfillResult.dry_run && (
              <div className="text-xs text-violet-700">Dry-run — no writes made. Click <b>Run</b> to commit.</div>
            )}
          </div>
        )}
      </section>

      <section data-testid="admin-users">
        <h2 className="text-xl font-semibold mb-3">Users · {users.length}</h2>
        <div className="overflow-hidden border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr><th className="px-3 py-2">Email</th><th className="px-3 py-2">Created</th><th className="px-3 py-2">Admin</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">{u.email}</td>
                  <td className="px-3 py-2 font-mono text-xs">{u.created_at?.slice(0, 10)}</td>
                  <td className="px-3 py-2">{u.is_admin ? "yes" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section data-testid="admin-ideas">
        <h2 className="text-xl font-semibold mb-3">Ideas · {ideas.length}</h2>
        <p className="text-xs text-slate-500 mb-2">
          Regenerate downloads the four Tier 1 documents for that idea directly to your machine.
          Admin bypass — works for any ring state. Checklist items may show Fail/Pending/[GAP] — expected.
        </p>
        {regenError && (
          <div
            className="mb-3 rounded-lg border-2 border-red-500 bg-red-50 text-red-900 p-3 text-sm"
            data-testid="admin-regen-error"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="mono-caps text-[0.65rem] text-red-700">
                Regenerate failed · {regenError.status} · {regenError.title || regenError.idea_id}
              </div>
              <button
                type="button"
                onClick={() => setRegenError(null)}
                data-testid="admin-regen-error-dismiss"
                className="text-xs underline hover:text-red-700"
              >
                dismiss
              </button>
            </div>
            <pre className="font-mono text-xs whitespace-pre-wrap break-words">{regenError.detail}</pre>
          </div>
        )}
        <div className="overflow-hidden border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2">Title</th><th className="px-3 py-2">Ring</th>
                <th className="px-3 py-2">Scores</th><th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2">Readiness</th><th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ideas.map((it) => {
                const t = it.tier_status || {};
                const tierLabel = t.tier2_complete
                  ? "Tier 2 complete"
                  : t.tier1_complete ? "Tier 1 complete" : "In progress";
                const scores = [1, 2, 3, 4].map((n) => it[`ring${n}_score`]?.score ?? "—").join(" · ");
                const readiness = it.panel_results?.readiness_label || "—";
                // current_ring=5 is a "past the end" sentinel meaning Gauntlet
                // complete. There is no Ring 5. Render it as "Complete" so
                // it stops reading like a ring the founder is doing.
                const ringLabel = (it.current_ring === 5 && it.status === "complete")
                  ? "Complete"
                  : `R${it.current_ring}`;
                return (
                  <tr key={it.id} className="border-t border-slate-100" data-testid={`admin-idea-${it.id}`}>
                    <td className="px-3 py-2 font-semibold">{it.title}</td>
                    <td className="px-3 py-2 font-mono text-xs">{ringLabel}</td>
                    <td className="px-3 py-2 font-mono text-xs">{scores}</td>
                    <td className="px-3 py-2">{tierLabel}</td>
                    <td className="px-3 py-2">{readiness}</td>
                    <td className="px-3 py-2">
                      <div className="flex gap-2">
                        <button type="button"
                          onClick={() => runDiagnostic(it.id, it.title)}
                          data-testid={`admin-diagnostic-${it.id}`}
                          className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100">
                          Diagnostic
                        </button>
                        <button type="button" disabled={busyIdea === it.id}
                          onClick={() => regen(it.id, it.title)}
                          data-testid={`admin-regen-${it.id}`}
                          className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">
                          {busyIdea === it.id ? "…" : "Regenerate & download"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {diagIdea && (
        <section
          ref={diagRef}
          className="rounded-xl border-2 border-slate-800 bg-slate-900 text-slate-100 p-5"
          data-testid="admin-diagnostic-panel"
        >
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="mono-caps text-[0.65rem] text-slate-400">Diagnostic · read-only</div>
              <h2 className="text-lg font-semibold">{diagIdea.title || diagIdea.id}</h2>
              <div className="mono-caps text-[0.6rem] text-slate-500">{diagIdea.id}</div>
              <a
                href={`${API}/admin/ideas/${diagIdea.id}/diagnostic`}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-diagnostic-raw-link"
                className="mono-caps text-[0.6rem] text-cyan-300 underline hover:text-cyan-200"
              >
                Open raw JSON in new tab ↗
              </a>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyDiagnostic}
                disabled={!diagJson}
                data-testid="admin-diagnostic-copy"
                className="px-3 py-1.5 text-xs rounded border border-slate-500 hover:bg-slate-800 disabled:opacity-40"
              >
                Copy JSON
              </button>
              <button
                type="button"
                onClick={() => { setDiagIdea(null); setDiagJson(null); setDiagError(null); }}
                data-testid="admin-diagnostic-close"
                className="px-3 py-1.5 text-xs rounded border border-slate-500 hover:bg-slate-800"
              >
                Close
              </button>
            </div>
          </div>
          {diagError && (
            <div
              className="text-xs font-mono bg-red-950 border-2 border-red-500 text-red-100 rounded p-3 mb-3"
              data-testid="admin-diagnostic-error"
            >
              <div className="text-red-300 mono-caps text-[0.65rem] mb-1">Fetch failed</div>
              <div>status: {String(diagError.status)}</div>
              <div>url: {diagError.url}</div>
              <div className="mt-1 whitespace-pre-wrap break-words">detail: {diagError.detail}</div>
            </div>
          )}
          <pre
            className="text-xs font-mono whitespace-pre-wrap break-words bg-slate-950 rounded p-3 max-h-[70vh] overflow-auto"
            data-testid="admin-diagnostic-json"
          >
            {diagBusy ? "Loading…" : (diagJson ? JSON.stringify(diagJson, null, 2) : (diagError ? "(no payload — see error above)" : "Loading…"))}
          </pre>
        </section>
      )}
    </div>
  );
}
